import { useTheme } from "@mui/material";
import { Add, ClipboardTick } from "iconsax-react";
import { orderBy } from "lodash";
import React from "react";
import { Prompt } from "react-router";

import AllTaskTableStateProvider from "@SonarCX/providers/AllTaskTableStateProvider";

import TaskMultiEdit from "@SonarCX/components/organisms/TaskMultiEdit";
import TaskTable from "@SonarCX/pages/Tasks/TaskTable";
import TaskModal from "../../TaskModal";

import { useAppDispatch, useAppSelector } from "@SonarCX/hooks/redux";
import useInfiniteFetch from "@SonarCX/hooks/useInfiniteFetch";
import { embedsToQuery } from "@SonarCX/lib/api";
import { DEFAULT_EMBEDS, useTaskAPI } from "@SonarCX/providers/TaskAPIProvider";
import { useTaskPageState } from "@SonarCX/providers/TaskPageStateProvider";
import { useTaskSWRMutateCache } from "@SonarCX/providers/TaskSWRMutateCache";
import {
  clearSelected,
  selectAllTasksTable,
} from "@SonarCX/slices/allTasksTable";
import useStyles from "./styles";

interface TasksSectionProps {
  userID: string;
  isContact?: boolean;
}

const TasksSection: React.FC<TasksSectionProps> = ({ userID, isContact }) => {
  const dispatch = useAppDispatch();

  const { selected } = useAppSelector(selectAllTasksTable);

  const { updateTasks } = useTaskSWRMutateCache();
  const { updateCacheFromSWR } = useTaskAPI();
  const { tab } = useTaskPageState();

  const theme = useTheme();

  const [taskModalOpen, setTaskModalOpen] = React.useState(false);

  const canFetch = React.useMemo(() => Boolean(userID), [userID]);

  const {
    data: userTasks,
    loaded: userTasksLoaded,
    fullPagination: userTasksPaginationFullPagination,
    ...userTasksPagination
  } = useInfiniteFetch(() => {
    if (!canFetch) {
      return null;
    }

    const userQuery = isContact ? "concerning_user_id" : "assigned_to[]";
    const url = `${embedsToQuery(DEFAULT_EMBEDS)}&${
      userQuery
    }=${userID}&include_closed=true`;

    return `userTasks:${url}`;
  });

  const tasks = React.useMemo(() => {
    const flatTasks = userTasks.flatMap((x) => x.data);

    const ordered = orderBy(
      flatTasks,
      ["completed_at", "due_date"],
      ["desc", "asc"],
    );
    return { data: ordered };
  }, [userTasks]);

  React.useEffect(() => {
    if (tasks) {
      updateCacheFromSWR(tasks);
    }
  }, [updateCacheFromSWR, tasks]);

  const classes = useStyles();

  return (
    <>
      <div className={classes.root}>
        <div className={classes.title}>
          <ClipboardTick
            variant="Bold"
            color="grey"
            size={theme.size.base}
            style={{ marginRight: "9px" }}
          />
          TASKS
          {tasks?.data?.length >= 0
            ? ` (${tasks.data.length} / ${
                userTasks[0]?.pagination?.total || 0
              })`
            : ""}
          {isContact && (
            <div
              onClick={() => setTaskModalOpen(true)}
              className={classes.addTask}
            >
              <Add
                size={theme.size.base}
                variant="Bold"
                color="grey"
                className="icon"
              />{" "}
              Add Task
            </div>
          )}
        </div>

        {selected.length > 0 && <TaskMultiEdit />}

        <div className={classes.table}>
          {/* @ts-ignore */}
          <AllTaskTableStateProvider tasks={tasks}>
            {/* @ts-ignore */}
            <TaskTable
              id="customer-tasks"
              loaded={userTasksLoaded}
              message="There are no tasks for this user"
              hideHeader
              onUserDetails
              disableSort
              tableHeight="unset"
              isInfinite
              isPaginatedInfiniteLoader
              {...userTasksPagination}
            />
          </AllTaskTableStateProvider>
        </div>
      </div>

      {/* @ts-ignore */}
      <TaskModal
        open={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        onTaskCreated={(task) => {
          updateTasks(tab, task);
          userTasksPagination.mutate();
        }}
        canEdit
        canUpdate
        assignClient={userID}
      />

      <Prompt
        when
        message={() => {
          dispatch(clearSelected());
          return true;
        }}
      />
    </>
  );
};

export default TasksSection;
